import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE = process.env.REACT_APP_SITE_URL || window.location.origin;

function upsert(selector, create, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    el.setAttribute('data-seo', '1');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function meta(name, content, property = false) {
  if (!content) return;
  const key = property ? 'property' : 'name';
  upsert(`meta[${key}="${name}"]`, () => document.createElement('meta'), { [key]: name, content });
}

/**
 * Head tags for a public page. `noindex` keeps portal / checkout pages out of search.
 * Titles are used verbatim — keep them under ~60 characters.
 */
export function Seo({ title, description, image, noindex = false, canonicalPath }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const url = `${SITE}${canonicalPath || pathname}`.replace(/\/+$/, '') || SITE;
    if (title) document.title = title;
    meta('description', description);
    meta('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    upsert('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    }, { href: noindex ? url : url || SITE });
    meta('og:title', title, true);
    meta('og:description', description, true);
    meta('og:url', url, true);
    if (image) meta('og:image', image, true);
    meta('twitter:title', title);
    meta('twitter:description', description);
  }, [title, description, image, noindex, canonicalPath, pathname]);

  return null;
}

/** Injects (and keeps in sync) one JSON-LD block, identified by `id`. */
export function JsonLd({ id, data }) {
  useEffect(() => {
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { el?.remove(); };
  }, [id, data]);

  return null;
}
